import { motion } from 'framer-motion';
import { ArrowRight, Check, MousePointer2 } from 'lucide-react';
import type { ReactNode } from 'react';

/** One full loop of the cursor-click-import choreography, in seconds. */
const CYCLE = 4;

function BrowserFrame({ domain, children }: { domain: string; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-premium dark:border-white/10 dark:bg-slate-950">
      <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-4 py-2.5 dark:border-white/10 dark:bg-white/[0.04]">
        <span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-white/20" />
        <span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-white/20" />
        <span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-white/20" />
        <span className="ml-2 font-mono text-xs text-slate-400">{domain}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

const importedBadges = ['2 variants imported', 'Images matched', 'Published'];

export default function ImportShowcase() {
  return (
    <div className="grid items-center gap-4 sm:grid-cols-[1fr_auto_1fr]">
      {/* Supplier listing, as found */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5 }}
      >
        <BrowserFrame domain="aliexpress.com">
          <div className="grid h-24 place-items-center rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 text-4xl dark:from-white/[0.06] dark:to-white/[0.02]">
            ⌚
          </div>
          <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
            2022 New Fashion Men Waterproof Smart Watch Bluetooth Heart Rate
            Sport Bracelet Wholesale Drop Shipping
          </p>
          <p className="mt-2 font-mono text-sm text-slate-400">$11.40</p>
          <span className="relative mt-3 inline-block">
            <motion.span
              className="inline-block rounded-md bg-[#008060] px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-md"
              animate={{ scale: [1, 1, 0.93, 1, 1] }}
              transition={{
                duration: CYCLE,
                times: [0, 0.34, 0.38, 0.42, 1],
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              Send product for review in Shopify (2 variants)
            </motion.span>
            <motion.span
              aria-hidden="true"
              className="pointer-events-none absolute left-[58%] top-[45%]"
              animate={{
                x: [44, 44, 2, 2, 2, 44],
                y: [40, 40, 2, 2, 2, 40],
                opacity: [0, 0, 1, 1, 1, 0],
              }}
              transition={{
                duration: CYCLE,
                times: [0, 0.1, 0.3, 0.42, 0.85, 1],
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <MousePointer2 className="h-[18px] w-[18px] fill-slate-900 stroke-white drop-shadow dark:fill-white dark:stroke-slate-900" />
            </motion.span>
          </span>
        </BrowserFrame>
      </motion.div>

      {/* Animated flow between the two */}
      <div className="relative hidden h-10 w-14 sm:block" aria-hidden="true">
        <ArrowRight
          size={20}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-300 dark:text-white/20"
        />
        <motion.span
          className="absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-teal-500"
          animate={{ x: [0, 0, 48, 48], opacity: [0, 0, 1, 0] }}
          transition={{
            duration: CYCLE,
            times: [0, 0.42, 0.62, 0.68],
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      {/* The reviewed product, live in the store */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ delay: 0.25, duration: 0.5 }}
      >
        <BrowserFrame domain="your-store.myshopify.com">
          <div className="grid h-24 place-items-center rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 text-4xl dark:from-teal-400/10 dark:to-teal-400/5">
            ⌚
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-950 dark:text-white">
            Apex Waterproof Fitness Smartwatch
          </p>
          <p className="text-[11px] text-slate-400">edited by you in review</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {importedBadges.map((badge, index) => (
              <motion.span
                key={badge}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + index * 0.18 }}
                className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700 dark:bg-green-400/10 dark:text-green-300"
              >
                <Check size={11} />
                {badge}
              </motion.span>
            ))}
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <p className="text-lg font-bold text-slate-950 dark:text-white">$29.00</p>
            <p className="font-mono text-[11px] text-teal-700 dark:text-teal-300">
              your markup applied
            </p>
          </div>
        </BrowserFrame>
      </motion.div>
    </div>
  );
}
